// src/components/DashboardCards/CatalogueHealthCard.js
import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import { FiLink } from 'react-icons/fi';
import AuthContext from '../../context/AuthContext';
import { useAmazonStatus } from '../../hooks/useAmazonStatus';
import useCatalogueHealthData from '../../pages/SellerIntelligence/CatalogueHealth/hooks/useCatalogueHealthData';
import styles from '../../pages/DashboardOverview.module.css';
import CatalogueHealthBg from '../../assets/dashboard_assets/catalogue_health.png';
import { AMAZON_MAINTENANCE_MODE, AMAZON_MAINTENANCE_MESSAGE } from '../../config/amazonMaintenance';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 

const CATALOGUE_HEALTH_PATH = '/dashboard/seller-intelligence/catalogue-health';

const CatalogueHealthCard = () => {
  const authCtx = useContext(AuthContext);
  const [isConnecting, setIsConnecting] = useState(false);

  const { connected: amazonConnected, loading: checkingAmazonStatus } = useAmazonStatus(authCtx.token);
  const { data: catalogueHealthData, loading } = useCatalogueHealthData(
    authCtx.token,
    amazonConnected,
    checkingAmazonStatus
  );

  const initiateAmazonLogin = async (token) => {
    const response = await fetch(`${API_URL}/auth/amazon/login`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Login failed: ${response.status}`);
    }

    return response.json();
  };

  const handleConnectAmazon = async () => {
    if (!authCtx.token) return;

    setIsConnecting(true);
    try {
      const data = await initiateAmazonLogin(authCtx.token);
      if (data.auth_url) {
        // Add a small delay to allow UI to update before redirecting
        setTimeout(() => {
          window.location.href = data.auth_url;
        }, 500);
      }
    } catch (error) {
      console.error('Error initiating Amazon login:', error);
      setIsConnecting(false);
    }
  };

  const MetricCard = ({ title, value, status, statusColor, hasArrow, hasWarning, barColor, showHoverDetails, hoverData }) => {
    const [isHovered, setIsHovered] = React.useState(false);
    
    return (
      <div 
        className={`${styles.metricCard} ${showHoverDetails ? styles.metricCardHoverable : ''}`}
        data-bar-color={barColor}
        onMouseEnter={() => showHoverDetails && setIsHovered(true)}
        onMouseLeave={() => showHoverDetails && setIsHovered(false)}
      >
        <div className={`${styles.metricCardIndicator} ${barColor === 'red' ? styles.metricCardIndicatorRed : ''}`}></div>
        {hasWarning && (
          <div className={styles.warningIcon}>
            <svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="12" height="12" rx="6" fill="url(#paint0_linear_102_901)"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M5.4295 3H6.5703C6.79306 3 6.96441 3.19153 6.93928 3.41315L6.59087 6.50131C6.56955 6.69018 6.41153 6.83106 6.22189 6.83106H5.7878C5.59818 6.83106 5.44053 6.69056 5.41882 6.50245L5.06089 3.41429C5.03461 3.19268 5.20637 3 5.4295 3ZM5.99991 7.52638C6.40697 7.52638 6.73672 7.85613 6.73672 8.26319C6.73672 8.67025 6.40697 9 5.99991 9C5.59285 9 5.2631 8.67025 5.2631 8.26319C5.2631 7.85651 5.59285 7.52638 5.99991 7.52638Z" fill="white"/>
              <defs>
                <linearGradient id="paint0_linear_102_901" x1="-4.47035e-08" y1="6" x2="11.0143" y2="12" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#FA9696"/>
                  <stop offset="1" stopColor="#DE524A"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
        )}
        {!(isHovered && showHoverDetails) && (
          <div className={styles.metricHeader}>
            <h4 className={styles.metricTitle}>
              {title}
              {hasArrow && <FaChevronRight className={styles.arrowIcon} />}
            </h4>
          </div>
        )}
        {isHovered && showHoverDetails && hoverData ? (
          <div className={styles.hoverDetailsContainer}>
            <div className={styles.hoverDetailSection}>
              <div className={styles.hoverDetailLabel}>Out of Stock</div>
              <div className={styles.hoverDetailValue}>{hoverData.outOfStock}</div>
            </div>
            <div className={styles.hoverDetailDivider}></div>
            <div className={styles.hoverDetailSection}>
              <div className={styles.hoverDetailLabel}>Suppressed</div>
              <div className={styles.hoverDetailValue}>{hoverData.suppressed}</div>
            </div>
          </div>
        ) : (
          <div className={styles.metricValueContainer}>
            <span className={styles.metricValue}>{value}</span>
            <span className={styles.metricStatus}>
              {/* <span className={styles.statusSeparator}>|</span>
              <span className={statusColor === 'green' ? styles.statusGreen : styles.statusRed}>
                {status}
              </span> */}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Maintenance state — show under-maintenance card instead of metrics or connect button.
  // Card is rendered non-interactive (no Link wrapper, no clickable button).
  if (AMAZON_MAINTENANCE_MODE) {
    return (
      <div
        className={styles.catalogueHealthCard}
        style={{ backgroundImage: `url(${CatalogueHealthBg})`, backgroundSize: '80%', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', cursor: 'not-allowed', opacity: 0.85 }}
        aria-label="Catalogue Health — under maintenance"
      >
        <div className={styles.catalogueHealthTitle}>
          <span>Catalogue Health</span>
        </div>
        <div className={styles.amazonConnectModal}>
          <div style={{ textAlign: 'center', padding: '12px 16px', color: '#505050', fontSize: '13px', fontWeight: 500, lineHeight: 1.4 }}>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#1B2833' }}>Under maintenance</div>
            <div>{AMAZON_MAINTENANCE_MESSAGE}</div>
          </div>
        </div>
      </div>
    );
  }

  // Show modal if Amazon is not connected OR checking status
  if (checkingAmazonStatus || !amazonConnected) {
    return (
      <div className={styles.catalogueHealthCard} style={{ backgroundImage: `url(${CatalogueHealthBg})`, backgroundSize: '80%', backgroundPosition: 'center', backgroundRepeat: 'no-repeat'}}>
        <div className={styles.catalogueHealthTitle}>
          <span>Catalogue Health</span>
        </div>
        <div className={styles.amazonConnectModal} >
          <button
            className={styles.connectAmazonButton}
            onClick={handleConnectAmazon}
            disabled={isConnecting}
          >
            <FiLink className={styles.linkIcon} />
            {isConnecting ? 'Connecting...' : 'Connect Amazon'}
          </button>
        </div>
      </div>
    );
  }

  const total = catalogueHealthData?.total || 0;
  const active = catalogueHealthData?.active || 0;
  const inactive = catalogueHealthData?.inactive || 0;
  const totalWarnings = catalogueHealthData?.suppression_analysis?.total_warnings || 0;
  const suppressed = catalogueHealthData?.suppression_analysis?.total_suppressed || catalogueHealthData?.suppressed || 0;
  const activePercent = total > 0 ? Math.round((active / total) * 100) : 0;
  const outOfStock = inactive - suppressed;

  return (
    <Link to={CATALOGUE_HEALTH_PATH} className={styles.catalogueHealthCard} aria-label="Open Catalogue Health">
      <div className={styles.catalogueHealthTitle}>
        <span>Catalogue Health</span>
        <span className={styles.catalogueHealthArrow}><FaChevronRight /></span>
      </div>
      <div className={styles.metricsGrid}>
        <MetricCard
          title="Total Listing"
          value={loading ? '...' : total.toLocaleString()}
          status="+5 This week"
          statusColor="green"
          barColor="blue"
        />
        <MetricCard
          title="Active Selection"
          value={loading ? '...' : active.toLocaleString()}
          status={loading ? 'Loading...' : `${activePercent}% of total`}
          statusColor="green"
          barColor="blue"
        />
        <MetricCard
          title="Inactive Selection"
          value={loading ? '...' : inactive.toLocaleString()}
          status="+5 This week"
          statusColor="red"
          hasArrow={true}
          barColor="red"
          showHoverDetails={true}
          hoverData={loading ? null : {
            outOfStock: outOfStock.toLocaleString(),
            suppressed: suppressed.toLocaleString()
          }}
        />
        <MetricCard
          title="Warnings"
          value={loading ? '...' : totalWarnings.toLocaleString()}
          // status="+22 vs Last Week"
          statusColor="red"
          hasWarning={true}
          barColor="red"
        />
      </div>
    </Link>
  );
};

export default CatalogueHealthCard;

